import { Component, OnInit } from '@angular/core';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { RuleInterface } from '@vendasta/automata';
import { AutomationVariableMenuComponent } from '../../../../../common/components/automation-variable-menu/automation-variable-menu.component';
import { insertVariable } from '../../../../../common/components/automation-variable-menu/automation-variable-menu.service';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { StaticSelectorService } from '../../../../../data-services/static-selector.service';
import { AutomationReplacementChipsComponent } from '../../../../../common/components';
import { ALL_VARIABLE_MENU_DATA_TYPES } from '../../../../../common/constants';
import {
  KeyValue,
  KeyValueForm,
  KeyValueInputComponent,
} from '../../../common/controls/key-value-input/key-value-input.component';

export const StringOperationUppercase = 'string-uppercase';
export const StringOperationCapitalize = 'string-capitalize';
export const StringOperationLowerCase = 'string-lowercase';
export const StringOperationDefaultValue = 'string-default-value';
export const StringOperationExtractEmail = 'string-extract-email';
export const StringOperationReplaceAll = 'string-replace-all';
export const StringOperationTableLookup = 'string-table-lookup';
export const StringOperations = new Map<string, string>([
  [StringOperationUppercase, 'AUTOMATIONS.EDITOR.TASKS.STRING_FORMATTER.OPERATORS.UPPERCASE'],
  [StringOperationCapitalize, 'AUTOMATIONS.EDITOR.TASKS.STRING_FORMATTER.OPERATORS.CAPITALIZE'],
  [StringOperationLowerCase, 'AUTOMATIONS.EDITOR.TASKS.STRING_FORMATTER.OPERATORS.LOWERCASE'],
  [StringOperationDefaultValue, 'AUTOMATIONS.EDITOR.TASKS.STRING_FORMATTER.OPERATORS.DEFAULT_VALUE'],
  [StringOperationExtractEmail, 'AUTOMATIONS.EDITOR.TASKS.STRING_FORMATTER.OPERATORS.EXTRACT_EMAIL_ADDRESS'],
  [StringOperationReplaceAll, 'AUTOMATIONS.EDITOR.TASKS.STRING_FORMATTER.OPERATORS.REPLACE'],
  [StringOperationTableLookup, 'AUTOMATIONS.EDITOR.TASKS.STRING_FORMATTER.OPERATORS.TABLE_LOOKUP'],
]);
export const StringOperationDescriptions = new Map<string, string>([
  [StringOperationUppercase, 'AUTOMATIONS.EDITOR.TASKS.STRING_FORMATTER.OPERATOR_DESCRIPTIONS.UPPERCASE'],
  [StringOperationCapitalize, 'AUTOMATIONS.EDITOR.TASKS.STRING_FORMATTER.OPERATOR_DESCRIPTIONS.CAPITALIZE'],
  [StringOperationLowerCase, 'AUTOMATIONS.EDITOR.TASKS.STRING_FORMATTER.OPERATOR_DESCRIPTIONS.LOWERCASE'],
  [StringOperationDefaultValue, 'AUTOMATIONS.EDITOR.TASKS.STRING_FORMATTER.OPERATOR_DESCRIPTIONS.DEFAULT_VALUE'],
  [
    StringOperationExtractEmail,
    'AUTOMATIONS.EDITOR.TASKS.STRING_FORMATTER.OPERATOR_DESCRIPTIONS.EXTRACT_EMAIL_ADDRESS',
  ],
  [StringOperationReplaceAll, 'AUTOMATIONS.EDITOR.TASKS.STRING_FORMATTER.OPERATOR_DESCRIPTIONS.REPLACE'],
  [StringOperationTableLookup, 'AUTOMATIONS.EDITOR.TASKS.STRING_FORMATTER.OPERATOR_DESCRIPTIONS.TABLE_LOOKUP'],
]);

@Component({
  selector: 'automata-string-operations-panel',
  templateUrl: './string-operations-panel.component.html',
  styleUrls: ['./string-operations-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    AutomationVariableMenuComponent,
    AutomationReplacementChipsComponent,
    KeyValueInputComponent,
  ],
})
export class StringOperationsPanelComponent extends CommonActionPanelComponent implements OnInit {
  allDataTypes = ALL_VARIABLE_MENU_DATA_TYPES;
  public StringOperationUppercase = StringOperationUppercase;
  public StringOperationCapitalize = StringOperationCapitalize;
  public StringOperationLowerCase = StringOperationLowerCase;
  public StringOperationDefaultValue = StringOperationDefaultValue;
  public StringOperationExtractEmail = StringOperationExtractEmail;
  public StringOperationReplaceAll = StringOperationReplaceAll;
  public StringOperationTableLookup = StringOperationTableLookup;

  stringOperations = StringOperations;
  stringOperationDescriptions = StringOperationDescriptions;

  operationControl: FormControl<string | null> = new FormControl<string>('', [Validators.required]);
  operatorsSelectorService = new StaticSelectorService(this.stringOperations, this.translateService);

  public operationFormControls: { [key: string]: UntypedFormControl | UntypedFormArray | UntypedFormGroup } = {
    [this.StringOperationUppercase]: new FormControl<string>('', [Validators.required]),
    [this.StringOperationCapitalize]: new FormControl<string>('', [Validators.required]),
    [this.StringOperationLowerCase]: new FormControl<string>('', [Validators.required]),
    [this.StringOperationExtractEmail]: new FormControl<string>('', [Validators.required]),
    [this.StringOperationDefaultValue]: new UntypedFormGroup(
      {
        value: new FormControl<string>('', [Validators.required]),
        defaultValue: new FormControl<string>('', [Validators.required]),
      },
      [Validators.required],
    ),
    [this.StringOperationReplaceAll]: new UntypedFormGroup(
      {
        value: new FormControl<string>('', [Validators.required]),
        toReplace: new FormControl<string>('', [Validators.required]),
        toReplaceWith: new FormControl<string>('', [Validators.required]),
      },
      [Validators.required],
    ),
    [this.StringOperationTableLookup]: new UntypedFormGroup(
      {
        value: new FormControl<string>('', [Validators.required]),
        tableData: new FormArray<FormGroup<KeyValueForm>>([]),
        defaultValue: new FormControl<string>(''),
      },
      [Validators.required],
    ),
  };

  constructor() {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    this.operationControl.setValue(jsonData.operation);
    switch (jsonData.operation) {
      case this.StringOperationUppercase:
      case this.StringOperationCapitalize:
      case this.StringOperationLowerCase:
      case this.StringOperationExtractEmail:
        this.operationFormControls[jsonData.operation].setValue(jsonData?.string_values?.[0]);
        break;
      case this.StringOperationDefaultValue: {
        const group = this.operationFormControls[jsonData.operation] as UntypedFormGroup;
        group.controls.value.setValue(jsonData?.string_values?.[0]);
        group.controls.defaultValue.setValue(jsonData?.string_values?.[1]);
        break;
      }
      case this.StringOperationReplaceAll: {
        const group = this.operationFormControls[jsonData.operation] as UntypedFormGroup;
        group.controls.value.setValue(jsonData?.string_values?.[0]);
        group.controls.toReplace.setValue(jsonData?.string_values?.[1]);
        group.controls.toReplaceWith.setValue(jsonData?.string_values?.[2]);
        break;
      }
      case this.StringOperationTableLookup: {
        const group = this.operationFormControls[jsonData.operation] as UntypedFormGroup;
        group.controls.value.setValue(jsonData?.string_values?.[0]);
        for (let i = 1; i < jsonData?.string_values?.length - 1; i += 2) {
          (group.controls.tableData as FormArray<FormGroup<KeyValueForm>>).push(
            new FormGroup<KeyValueForm>({
              key: new FormControl<string>(jsonData?.string_values?.[i]),
              value: new FormControl<string>(jsonData?.string_values?.[i + 1]),
            }),
          );
        }
        group.controls.defaultValue.setValue(jsonData?.string_values?.[jsonData?.string_values?.length - 1]);
        break;
      }
    }

    this.savingEnabled = true;
  }

  getRules(): RuleInterface[] {
    return [];
  }

  getData() {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    jsonData.operation = this.operationControl.value;
    switch (this.operationControl.value) {
      case this.StringOperationUppercase:
      case this.StringOperationCapitalize:
      case this.StringOperationLowerCase:
      case this.StringOperationExtractEmail:
        jsonData.string_values = [this.operationFormControls[this.operationControl.value].value];
        break;
      case this.StringOperationDefaultValue: {
        const group = this.operationFormControls[this.operationControl.value] as UntypedFormGroup;
        jsonData.string_values = [group.controls.value.value, group.controls.defaultValue.value];
        break;
      }
      case this.StringOperationReplaceAll: {
        const group = this.operationFormControls[this.operationControl.value] as UntypedFormGroup;
        jsonData.string_values = [
          group.controls.value.value,
          group.controls.toReplace.value,
          group.controls.toReplaceWith.value,
        ];
        break;
      }
      case this.StringOperationTableLookup: {
        const group = this.operationFormControls[this.operationControl.value] as UntypedFormGroup;
        jsonData.string_values = [];
        jsonData.string_values.push(group.controls.value?.value);
        group.controls.tableData?.value?.forEach((kv: KeyValue) => {
          jsonData.string_values.push(kv?.key);
          jsonData.string_values.push(kv?.value);
        });
        jsonData.string_values.push(group.controls.defaultValue?.value);
        break;
      }
    }

    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    const operatorFormControl = this.operationFormControls?.[this.operationControl.value];
    return [this.operationControl, operatorFormControl];
  }

  isFormValid(): boolean {
    const operatorFormControl = this.operationFormControls?.[this.operationControl.value];
    return this.operationControl.valid && operatorFormControl.valid;
  }

  insertVariable(elementID: string, control: AbstractControl, variable: string): void {
    insertVariable(elementID, control, variable);
  }

  protected readonly ALL_VARIABLE_MENU_DATA_TYPES = ALL_VARIABLE_MENU_DATA_TYPES;
  protected readonly UntypedFormGroup = UntypedFormGroup;
}
